export const SESSION_MS = 60_000;
export const PLAYER_WIDTH = 0.1;
export const PLAYER_TOP = 0.84;
export const ITEM_SIZE = 0.1;
export const ENTRIES = [
    { label: 'Attendee badge', points: 10 },
    { label: 'VIP pass', points: 25 },
    { label: 'Broken QR code', points: -10 },
    { label: 'Spam registration', points: -15 },
] as const;

export type FallingEntry = { id: number; kind: number; x: number; y: number };
export type GameState = {
    phase: 'ready' | 'playing' | 'finished';
    score: number;
    best: number;
    streak: number;
    remaining: number;
    started: number;
    lastFrame: number;
    nextSpawn: number;
    nextId: number;
    player: number;
    items: FallingEntry[];
    feedback: string;
    caught: number;
    saved: number;
    lastPoints: number;
    record: boolean;
};
export type GameAction =
    | { type: 'start'; now: number; best: number }
    | { type: 'frame'; now: number; direction: number; randomKind: number; randomX: number };

export function difficulty(elapsed: number) {
    const level = Math.min(5, Math.floor(Math.max(0, elapsed) / 10_000));
    return { level, spawnMs: 950 - level * 120, speed: 0.18 + level * 0.035 };
}

export function initialGame(best: number): GameState {
    return {
        phase: 'ready', score: 0, best, streak: 0, remaining: 60, started: 0,
        lastFrame: 0, nextSpawn: 0, nextId: 0, player: 0.5, items: [],
        feedback: '', caught: 0, saved: 0, lastPoints: 0, record: false,
    };
}

export function gameReducer(state: GameState, action: GameAction): GameState {
    if (action.type === 'start') {
        return {
            ...initialGame(Math.max(state.best, action.best)), phase: 'playing',
            started: action.now, lastFrame: action.now, nextSpawn: action.now,
        };
    }
    if (state.phase !== 'playing') return state;
    const elapsed = action.now - state.started;
    if (elapsed >= SESSION_MS) {
        return { ...state, phase: 'finished', remaining: 0, items: [], best: Math.max(state.best, state.score), record: state.score > state.best };
    }
    const delta = Math.min(0.05, Math.max(0, action.now - state.lastFrame) / 1000);
    const player = Math.max(PLAYER_WIDTH / 2, Math.min(1 - PLAYER_WIDTH / 2, state.player + action.direction * delta * 0.85));
    const { spawnMs, speed } = difficulty(elapsed);
    let { score, streak, feedback, caught, saved, lastPoints, nextSpawn, nextId } = state;
    const items: FallingEntry[] = [];

    for (const item of state.items) {
        const y = item.y + speed * delta;
        const entry = ENTRIES[item.kind];
        const overlaps = Math.abs(item.x - player) < (PLAYER_WIDTH + ITEM_SIZE) / 2;
        if (overlaps && y + ITEM_SIZE >= PLAYER_TOP && item.y <= PLAYER_TOP + ITEM_SIZE) {
            score += entry.points;
            streak = entry.points > 0 ? streak + 1 : 0;
            caught += 1;
            saved += Number(entry.points > 0);
            lastPoints = entry.points;
            feedback = `${entry.label}: ${entry.points > 0 ? '+' : ''}${entry.points}. ${entry.points > 0 ? `${streak} streak.` : 'Combo reset.'}`;
        } else if (y > 1) {
            if (entry.points > 0) {
                streak = 0;
                feedback = `${entry.label} missed. Combo reset.`;
            }
        } else {
            items.push({ ...item, y });
        }
    }

    if (action.now >= nextSpawn) {
        items.push({
            id: nextId, kind: Math.min(3, Math.floor(action.randomKind * ENTRIES.length)),
            x: ITEM_SIZE / 2 + action.randomX * (1 - ITEM_SIZE), y: -ITEM_SIZE,
        });
        nextId += 1;
        nextSpawn = action.now + spawnMs;
    }

    return {
        ...state, player, items, score, streak, feedback, caught, saved, lastPoints, nextSpawn, nextId,
        remaining: Math.ceil((SESSION_MS - elapsed) / 1000), lastFrame: action.now,
    };
}